"use client";

import React from "react";
import SafeHtml from "@/components/ui/SafeHtml";

type Props = {
  idPrefix: string;
  label: string; // SafeHtml string
  required?: boolean;

  length?: number; // default 6
  value: string[]; // ["1","2","3","4","5","6"]
  onChange: (next: string[]) => void;

  invalid?: boolean; // drives red ring/border
  error?: string | null; // message under boxes
  onClearError?: () => void; // called when user edits/pastes
};

export default function OtpInput({
  idPrefix,
  label,
  required = true,
  length = 6,
  value,
  onChange,
  invalid = false,
  error,
  onClearError,
}: Props) {
  const refs = React.useRef<Array<HTMLInputElement | null>>([]);
  refs.current = refs.current.slice(0, length);

  function focusAt(index: number) {
    const el = refs.current[index];
    el?.focus?.();
    el?.setSelectionRange?.(0, 1);
  }

  function setAt(index: number, digit: string) {
    onChange(
      Array.from({ length }, (_, i) => (i === index ? digit : value[i] ?? ""))
    );
  }

  function spreadFrom(index: number, digits: string) {
    const spread = digits.slice(0, length - index).split("");
    onChange(
      Array.from({ length }, (_, i) => {
        const current = value[i] ?? "";
        const j = i - index;
        if (j >= 0 && j < spread.length) return spread[j];
        return current;
      })
    );

    const nextIndex = Math.min(index + spread.length, length - 1);
    focusAt(nextIndex);
  }

  function handleChange(index: number, raw: string) {
    onClearError?.();

    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      setAt(index, "");
      return;
    }

    if (digits.length === 1) {
      setAt(index, digits);
      if (index < length - 1) focusAt(index + 1);
      return;
    }

    spreadFrom(index, digits);
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      if (value[index]) {
        e.preventDefault();
        setAt(index, "");
        return;
      }
      if (index > 0) {
        e.preventDefault();
        setAt(index - 1, "");
        focusAt(index - 1);
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusAt(index - 1);
    }

    if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      focusAt(index + 1);
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    const text = e.clipboardData.getData("text");
    const digits = text.replace(/\D/g, "").slice(0, length);
    if (!digits) return;

    e.preventDefault();
    onClearError?.();

    const arr = digits.split("");
    onChange(Array.from({ length }, (_, i) => arr[i] ?? ""));

    const lastFilled = Math.min(arr.length, length) - 1;
    focusAt(Math.max(0, lastFilled));
  }

  return (
    <div className="w-full min-w-0" onPaste={handlePaste}>
      <label className="inline-flex items-center">
        <SafeHtml content={label} />
        {required ? (
          <span
            aria-hidden="true"
            className="text-fuji-red leading-none relative -top-[1px]"
          >
            *
          </span>
        ) : null}
      </label>

      <div className="mt-2 flex justify-center gap-2">
        {Array.from({ length }).map((_, i) => (
          <input
            key={i}
            id={`${idPrefix}-${i}`}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            value={value[i] ?? ""}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onFocus={() => {
              setTimeout(() => {
                refs.current[i]?.setSelectionRange?.(0, 1);
              }, 0);
            }}
            aria-invalid={invalid || undefined}
            className={[
              "h-[48px] w-12 rounded-[10px] border bg-white focus:outline-none",
              "text-center text-title-4 tracking-widest",
              invalid
                ? "border-transparent ring-2 ring-fuji-red focus:ring-2 focus:ring-fuji-red"
                : "border-[#d9d9d9] ring-0 focus:border-transparent focus:ring-2 focus:ring-black",
            ].join(" ")}
          />
        ))}
      </div>

      {error ? (
        <small role="alert" className="mt-2 block text-body-3 text-fuji-red text-left">
          {error}
        </small>
      ) : null}
    </div>
  );
}
